import React from 'react';
import hbondimage from '../assets/hbonds.png'
import saltbridges from '../assets/saltbridges.png'
import watermediated from '../assets/watermediated.png'
import chon from '../assets/chon.png'
import disufidebridges from '../assets/disulfidebridges.png'
import halogen from '../assets/halogen.png'
import metalmediated from '../assets/metalmediated.png'
import piinteraction from '../assets/piinteraction.png'
import cationpi from '../assets/cationpi.png'
import anionpi from '../assets/anionpi.png'
import polarpi from '../assets/polarpi.png'
import lppi from '../assets/lppi.png'
import pipi from '../assets/pipi.png'
import chpi from '../assets/chpi.png'
import noshpi from '../assets/noshpi.png'
import apolar from '../assets/apolar.png'
import polar from '../assets/polar.png'

export default function About() {
    return (
        <>
            <div style={{padding: '30px'}}>
                <h1>Welcome to COCOMAPS 2.0</h1>
                <p>
                    COCOMAPS (bioCOmplexes COntact MAPS) 2.0 is a web server to easily and effectively identify, analyze and visualize interactions at the interface of biocomplexes consisting of protein, RNA and/or DNA molecules, with
                    cutting-edge flexibility, interactivity, and efficiency of the graphical visualizations.
                </p>

                <h2 style={{color: 'red'}}>
                    Key Features of COCOMAPS 2.0:
                </h2>

                <h3>
                    Comprehensive Contact Analysis:
                </h3>
                <ul>
                    <li>Identifies atomic interactions at the interface of protein-protein and protein-nucleic acid complexes.</li>
                    <li>Default detection range of 5 Å, adjustable based on user preferences.</li>
                    <li>Detects 16 different types of specific atomic interactions, plus “proximal” contacts (see below) and clashes.</li>
                    <li>Generates detailed tables and efficient visualizations of the interactions for easy interpretation.</li>
                </ul>

                <h3>Interactive and Advanced Visualization:</h3>
                <ul>
                    <li>Provides an interactive Mol* visualization, which encompasses all the atomic interactions at the interface.</li>
                    <li>Features hyperlinks for seamless navigation between 3D view and tables.</li>
                    <li>Supplies multiple summary representations of the interface, as a pie chart, a heatmap, a 2D and a 3D atomic contact map.</li>
                </ul>

                <h3>Downloadable Output:</h3>
                <ul>
                    <li>Easily download files containing atomic contact information, distances, and angles.</li>
                    <li>Facilitate further analyses with accessible, parsable data formats.</li>
                </ul>

                <p>
                    COCOMAPS 2.0 is designed for speed and precision, making it an invaluable resource for researchers
                    investigating the intricate interfaces of protein-protein and protein-nucleic acid interactions. Explore
                    your structures in-depth and uncover new insights with our advanced features and user-friendly
                    interface.
                </p>

                <p>
                    As a first step, the server generates a:
                </p>

                <h2>Table of Atomic Interactions</h2>

                <p>
                    Based on a cut-off distance between any pair of heavy (non-hydrogen) atoms from the chains whose interaction interface is under analysis, an overall list (and map) of residues contacting each other at the interface is
                    derived. The default cut-off value is 5 Å, but it can be modified by the user.
                    <br/><br/>
                    Each pair of contacting residues at the interface is then tagged with its associated specific atomic interactions, as detailed below. All contacts occurring within the cut-off distance and not falling in any class of the
                    considered atomic interactions, are categorized as “proximal” (see below).
                    <br/><br/>
                    In the following, all the types of atomic interactions identified and visualized by the web server are listed along with the parameters used to define them (which can be however customized by the user).
                </p>

                {/* H-BONDS */}
                <h2>H-bonds</h2>
                <h5>Reference DOI: 10.1006/jmbi.1994.1334</h5>

                <p>
                    Hydrogen bonds form due to an electrostatic attraction between a hydrogen atom covalently bonded to a highly electronegative atom, such as O or N (H-bond donor, D) and another non-covalently bonded electronegative atom
                    (H-bond acceptor, A).
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={hbondimage} alt='h-bond'></img>
                </div>

                <p>
                    To identify hydrogen bonds, we used HBPLUS v3.2. The following geometric criteria were set up:
                </p>
                <ol>
                    <li>The distance between the donor (D) and the acceptor (A) atoms must be within 4.0 Å.</li>
                    <li>The θ angle formed by the donor (D), hydrogen (H), and acceptor (A) atoms must exceed 90°.</li>
                </ol>

                <p>Editable user-defined variables: H-bond dist, H-bond angle</p>
                {/* H-BONDS */}

                {/* Salt-bridges  */}
                <h2>Salt-bridges</h2>
                <h5>Reference DOI: 10.1006/jmbi.1994.1334</h5>

                <p>
                    Salt bridges arise from a strong electrostatic attraction between positively and negatively charged atoms.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={saltbridges} alt='Salt-bridges'></img>
                </div>

                <p>
                    In the context of protein-protein complexes, a salt bridge may occur between the positively charged amino groups (NH3+) and the negatively charged carboxyl groups (COO-) of amino acids. In protein-nucleic acid (NA)
                    complexes, the negatively charges are provided by the NA phosphate groups (OP1/OP2 atoms).
                    <br/><br/>
                    Our tool specifically identifies salt bridges between the positive charges on the side chains of Lys and Arg and the negative charges on the side chains of Asp and Glu or on the phosphate groups of the four nucleotides
                    (G, C, A, T/U).
                    <br/><br/>
                    <p>
                        To identify salt bridges, we used HBPLUS v3.2. The following geometric criterion was set up:
                    </p>
                    <ol>
                        <li>The distance between the positive and negative atoms involved in the interaction must be within 4.5 Å.</li>
                    </ol>
                    Editable user-defined variables: Salt-bridge dist
                </p>
                {/* Salt-bridges  */}

                {/* Water-mediated contacts  */}
                <h2>Water-mediated contacts</h2>
                <h5>Reference DOI: 10.1006/jmbi.1994.1334</h5>

                <p>
                    Water-mediated contacts contribute to the overall stability and functionality of biomolecular complexes. In these contacts, water molecules act as bridges between two electronegative atoms by H-bonding to them.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={watermediated} alt='Water-mediated contacts'></img>
                </div>
                <p>
                    To identify water-mediated contacts, we used HBPLUS v3.2. The following geometric criterion was set up:
                </p>
                <ol>
                    <li>
                        The distance between the oxygen atom of the water molecule and the H-bonded biomolecule electronegative atoms must be within 4.0 Å.
                    </li>
                </ol>
                <p>
                    Editable user-defined variables: Water mediated dist
                    Reference DOI: 10.1002/prot.24439
                </p>
                {/* Water-mediated contacts  */}

                {/* CH-O/N bonds */}
                <h2>CH-O/N bonds</h2>
                <h5>Reference DOI: https://doi.org/10.1021/ar950135n</h5>
                <h5>Reference DOI: https://journals.iucr.org/services/cif/hbonds.html</h5>

                <p>
                    C-H-O/N hydrogen bonds form due to a weak electrostatic attraction between a hydrogen (H) atom covalently bonded to a C, the donor, and a highly electronegative atom X (i.e. O or N), the acceptor. Despite being weaker as
                    compared to proper H-bonds (see above), these interactions play important roles in supramolecular chemistry and molecular recognition in biomolecules.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={chon} alt='CH-O/N bonds'></img>
                </div>
                <p>
                    To identify CH...O/N bonds, the following geometric criteria were set up:
                </p>
                <ol>
                    <li>The distance between the carbon (C) atom and the acceptor atom (X), denoted as d(CX), must be within 4.0 Å.</li>
                    <li>The θ angle formed by the carbon (C), hydrogen (H), and acceptor (X) atoms must exceed 110°.</li>
                </ol>
                <p>
                    Editable user-defined variables: CH-O/N dist, CH-O/N theta
                </p>
                {/* CH-O/N bonds */}

                {/* Disulfide bridges (SS-bonds) */}
                <h2>Disulfide bridges (SS-bonds)</h2>
                <h5>Reference DOI: 10.1016/0022-2836(81)90515-5</h5>

                <p>
                    Disulfide bridges, also abbreviated as SS-bonds, are the only covalent bonds considered here. They form intra- or inter-molecularly between sulfur atoms at the  position (SG) of Cys side chains.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={disufidebridges} alt='CH-O/N bonds'></img>
                </div>
                <p>
                    To detect disulfide bridges, the following geometrical criterion was set up:
                </p>
                <ol>
                    <li>The distance between the SG atoms on two Cys residues must be within 3.0 Å.</li>
                </ol>
                <p>Editable user-defined variables: Disulfide bridge dist</p>
                {/* Disulfide bridges (SS-bonds) */}

                {/* Halogen bonds */}
                <h2>Halogen bonds</h2>
                <p>
                    Halogen bonds are non-covalent interactions due to the electrostatic attraction between a halogen atom Z (i.e. F, Cl, Br or I) covalently bonded to a C atom and an electronegative atom X (i.e. O, N or S), in turn
                    covalently bonded to a C/P/S (Y) atom.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={halogen} alt='halogen bonds'></img>
                </div>
                <p>To detect halogen bonds, the following geometrical criteria were set up:</p>
                <ol>
                    <li>The distance between X and Z must be within the sum of their respective van der Waals radii
                        (for adopted van der Waals radii please see the bottom of this documentation);
                    </li>
                    <li>The θ1 angle formed by the C, Z and X atoms must exceed 165°;</li>
                    <li>The θ2 angle formed by the Z, X and Y atoms must exceed 120°.</li>
                </ol>
                <p>
                    Reference DOI: 10.1073/pnas.0407607101
                </p>
                <p>
                    Editable user-defined variables: Halogen theta 1, Halogen theta 2
                </p>
                {/* Halogen bonds */}

                {/* Metal-mediated contacts */}
                <h2>Metal-mediated contacts</h2>
                <h5>Reference DOI: 10.1002/pro.727</h5>

                <p>
                    Metal-mediated contacts are interactions where metal (M) cations act as bridges or mediators between two molecules, by coordinating to electronegative atoms X (O, N or S) on them.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={metalmediated} alt='metal mediated'></img>
                </div>
                <p>To identify metal-mediated contacts, the following criterion was set up:</p>
                <ol>
                    <li>The distance between the M ion and the two X atoms must be within 3.2 Å.</li>
                </ol>
                <p>
                    Van der Waals radii are not used here due to the uncertainties about the charge and oxidation state of metal ions in the structures.
                </p>
                <p>
                    Editable user-defined variables: Metal dist
                </p>
                {/* Metal-mediated contacts */}

                {/* Geometrical frame for π (Pi) interactions */}
                <h3>Geometrical frame for π (Pi) interactions</h3>
                <p>
                    π (Pi) interactions may involve the π cloud of the single or double aromatic ring of the nucleobases: G, U, A, T/U in nucleic acids, and aromatic rings of the amino acids: His, Phe, Trp, and Tyr in proteins. To detect
                    such interactions, we set up a geometrical frame on the π rings, both single and double, as detailed below.
                </p>
                <p>
                    For each aromatic ring, we assign its geometrical center (centroid), then define a ring plane (shown as a gray grid in Figure below), passing through the above centroid and the two ring atoms listed first in the
                    corresponding .pdb/.cif file. Then, to define whether an interaction exists between a given atom/ion and the aromatic ring, we orthogonally project the atom into the ring plane and measure this orthogonal distance. The
                    interaction is assigned if: i) the orthogonal distance is within a given threshold, and ii) the projection falls within the perimeter of the ring (to this aim we use the ConvexHull function available from the SciPy
                    library in Python (https://docs.scipy.org/doc/scipy/reference/generated/scipy.spatial.ConvexHull.html).
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={piinteraction} alt='pi interaction'></img>
                </div>
                <p>
                    All the π (Pi) interactions listed below (including Cation-π, Anion-π, Lone pair-π, Amino-π) refer to this geometrical frame for the aromatic rings.
                </p>
                {/* Geometrical frame for π (Pi) interactions */}

                {/* Cation-π interactions */}
                <h2>Cation-π interactions</h2>
                <p>
                    Cation-π (stacking) interactions are due to the possible attraction between a positive charge (cation) and the π cloud of an aromatic ring. The positive charge on the side chains of Arg (NE, CZ, NH1, NH2), Lys (CE, NZ)
                    and are considered as cations in the present study.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={cationpi} alt='cation-pi interaction'></img>
                </div>
                <p>
                    Once the Geometrical frame of the single or double aromatic ring (here represented for the sake of simplicity as a hexagon) was set up as detailed above, the following criterion was used:
                </p>
                <ol>
                    <li>The vertical distance between the cation and the ring plane (d) must be within 5.0 Å.</li>
                </ol>
                <p>
                    Editable user-defined variables: CATION_PI_DIST
                </p>
                <p>
                    Reference DOI: For choice of cation atoms: 10.1093/nar/gkh733
                </p>
                {/* Cation-π interactions */}

                {/* Anion-π interactions */}
                <h2>Anion-π interactions</h2>
                <p>
                    Anion-π (stacking) interactions are due to the possible attraction between a negative charge (anion) and the π cloud of an aromatic ring. The negative charges on the side chains of Glu (OE1, OE2 atoms) and Asp (OD1, OD2
                    atoms) and on the DNA/RNA phosphate groups (OP,/OP2 atoms) have been considered here.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={anionpi} alt='anion-pi interaction'></img>
                </div>
                <p>
                    Once the Geometrical frame of the single or double aromatic ring (here represented for the sake of simplicity as a hexagon) was set up as detailed above, the following criterion was used:
                </p>
                <ol>
                    <li>The vertical distance between the anion and the ring plane (d) must be within 5.0 Å.</li>
                </ol>
                <p>
                    Editable user-defined variables: ANION_PI_DIST
                </p>
                <p>
                    Reference DOI: https://doi.org/10.1093/nar/gkx757
                </p>
                {/* Anion-π interactions */}

                {/* Amino-π interactions */}
                <h2>Amino-π interactions</h2>
                <h5>Reference DOI: 10.1002/prot.24527</h5>

                <p>
                    Amino–π (stacking) interactions arise from the attractive forces between an amino group and the π-electron cloud of an aromatic ring. The following atoms are considered to give amino groups: ND1 of Asn, NE2 of Gln, N2 of
                    guanine (G), N6 of adenine (A), and N4 of cytosine (C).
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={polarpi} alt='amino-pi interaction'></img>
                </div>
                <p>
                    Once the Geometrical frame of the single or double aromatic ring (here represented for the sake of simplicity as a hexagon) was set up as detailed above, the following criterion was used:
                </p>
                <ol>
                    <li>1. The vertical distance between the amino atom and the ring plane (d) must be within 5.0 Å.</li>
                </ol>
                <p>
                    Editable user-defined variables: AMINO_PI_DIST
                </p>
                {/* Amino-π interactions */}

                {/* lp-π interactions */}
                <h2>lp-π interactions</h2>
                <h5>Reference DOI: https://doi.org/10.1093/nar/gkx757</h5>

                <p>
                    LonePair-π (stacking) interactions are due to the possible attraction between an electric lone pair (on an atom LP) and the π cloud of an aromatic ring. LonePair-presenting atoms considered here are any O,S,N atoms
                    featuring at least an electron lone pair.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={lppi} alt='lp-pi interaction'></img>
                </div>
                <p>
                    Once the Geometrical frame of the single or double aromatic ring (here represented for the sake of simplicity as a hexagon) was set up, to define lp-pi interactions, the following criterion was used:
                </p>
                <ol>
                    <li>The vertical distance between the LonePair-presenting atom (Y) and the ring plane (d) must be within ± 5.0 Å.</li>
                </ol>
                <p>
                    Editable user-defined variables: LONEPAIR_PI_DIST
                </p>
                {/* lp-π interactions */}

                {/* π-π interactions */}
                <h2>π-π interactions</h2>
                <p>
                    π-π (stacking) interactions are due to the attraction between the π cloud of aromatic rings situated in close proximity.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={pipi} alt='pi-pi interaction'></img>
                </div>
                <p>
                    To describe the orientation between two aromatic rings, we have specifically employed three parameters – a distance and two angles – to characterize the intermolecular orientation of one aromatic ring relative to
                    another, thereby defining a π-π contact between the aromatic rings.
                </p>
                <ol>
                    <li>The distance between the centroids of the two aromatic rings (R<sub>cen</sub>) must be within 5.5 Å.</li>
                    <li>The center-normal angle (θ, between the normal to the ring and the line connecting the centroids of the two rings) must be within 80°.</li>
                    <li>The normal-normal angle (γ, between the normal to ring-1 and the normal to ring-2) must be within 90°.</li>
                </ol>
                <p>
                    Editable user-defined variables: PI_PI_DIST, PI_PI_THETA, PI_PI_GAMMA
                    <br/>
                    Ref DOI: 10.1074/jbc.273.25.15458
                </p>
                {/* π-π interactions */}

                {/* CH-π interactions */}
                <h2>CH-π interactions</h2>
                <p>
                    CH-π (CH-Pi) interactions form due to the attraction between a H atom covalently bonded to a C atom (C-H) and the π cloud of an aromatic ring. This interaction involves the alignment of the hydrogen atom with the
                    aromatic ring, resulting in attractive forces between them. C-H…π interactions are characterized by their relatively weak strength compared to traditional hydrogen bonds but can significantly contribute to the stability
                    of molecular complexes, protein-ligand interactions, and other biomolecular assemblies.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={chpi} alt='ch-pi interaction'></img>
                </div>
                <p>
                    To define CH-π interactions, the following three criteria were used:
                </p>
                <ol>
                    <li>The distance between the C atom and the π<sub>m</sub> (represents the ring mid-point of aromatic ring) must be within 4.5 Angstroms.</li>
                    <li>The θ1 angle between the atom C, π<sub>m</sub> and the π<sub>n</sub> (represents the normal to the plane of the ring) must be within 30 degrees.</li>
                    <li>3. The θ2 angle between the vector along the C-H bond and the vector along the distance between H and the π<sub>m</sub> must exceed 120° (θ2 &ge; 120°).</li>
                </ol>
                <p>
                    Editable user-defined variables: CH-Pi dist, CH-Pi theta 1, CH-Pi theta 2
                </p>
                <p>
                    Reference: https://doi.org/10.1006/jmbi.2000.4473
                </p>
                {/* CH-π interactions */}

                {/* N/O/SH-π interactions */}
                <h2>N/O/SH-π interactions</h2>
                <p>
                    N/O/SH-π (N/O/SH-Pi) interactions are due to the attraction between a H atom covalently bonded to an O, N or S (X) atom and the π-cloud of an aromatic ring.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={noshpi} alt='N/O/SH-Pi interaction'></img>
                </div>
                <p>
                    To define N/O/SH-π interactions, the following three criteria were used:
                </p>
                <ol>
                    <li>The distance between the X atom and the origin of the Cartesian frame on the aromatic ring must be within 4.5 Angstroms.</li>
                    <li>The θ<sub>1</sub> angle between the X atom, the origin of the Cartesian frame and the z-axis must be within 30 degrees.</li>
                    <li>The θ<sub>2</sub> angle between the vector along the X-H bond and the vector along the distance between H and the origin of the Cartesian frame must exceed 120° (θ2 &ge; 120°).</li>
                </ol>
                <p>
                    Editable user-defined variables: N/O/S-Pi dist, N/O/S-Pi teta 1, N/O/S-Pi teta 2
                </p>
                <p>
                    Reference DOI: 10.1006/jmbi.2000.4301; 10.1093/nar/gkg528
                </p>
                {/* N/O/SH-π interactions */}

                {/*Apolar (van der Waals) contacts */}
                <h2>Apolar (van der Waals) contacts</h2>
                <p>
                    Apolar contacts are van der Waals (vdW) interactions between two apolar atoms. Herein, we consider as apolar those C atoms covalently bonded to other C or H atoms only. Apolar interactions between rings interacting via
                    π-π stacking (see above) are not considered here.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={apolar} alt='apolar contacts'></img>
                </div>
                <p>
                    To identify apolar contacts, the following criterion was set up:
                </p>
                <ol>
                    <li>The distance between the two ineracting apolar atoms must be within the sum of their vdW radii plus a tolerance (d) of 0.5 Å. With the vdW radius used here for C (1.7 Å, see at the bottom of this page for a complete
                        list), this amounts to 3.9 Å.
                    </li>
                </ol>
                <p>
                    Editable user-defined variables: Apolar tolerance
                </p>
                <p>
                    Reference: 10.1093/nar/gkv315
                </p>
                {/*Apolar (van der Waals) contacts */}

                {/*Polar (van der Waals) contacts */}
                <h2>Polar (van der Waals) contacts</h2>
                <h5>Reference DOI: 10.1093/nar/gkv315</h5>

                <p>
                    Polar contacts are van der Waals (vdW) interactions between two atoms of which at least one is not classified as apolar (see above). Polar interactions between rings interacting via - stacking (see above) are not
                    considered here.
                </p>
                <div style={{textAlign: 'center'}}>
                    <img src={polar} alt='polar contacts'></img>
                </div>
                <p>To identify polar contacts, the following criterion was set up:</p>
                <ol>
                    <li>The distance between the two interacting atoms must be within the sum of their vdW radii plus a tolerance (d) of 0.5 Å. For the vdW radii used here see the bottom of this page.</li>
                </ol>
                <p>Editable user-defined variables: Polar tolerance</p>
                {/*Polar (van der Waals) contacts */}

                {/* Proximal contacts  */}
                <h2>Proximal contacts</h2>
                <h5>Reference DOI: https://doi.org/10.1016/j.jmb.2016.12.004</h5>

                <p>All contacts occurring within the chosen cut-off distance (5 Å, being the default value here), and not falling in any of the atomic interactions described above, are classified as proximal.</p>
                {/* Proximal contacts  */}

                {/* Clashes */}
                <h2>Clashes</h2>
                <h5>Reference DOI: https://doi.org/10.1016/j.jmb.2016.12.004</h5>

                <p>Contacts between heavy atoms occuring at a distance below the sum of their vdW radii are classified as clashes.</p>
                {/* Clashes */}

                {/* List of van der Waals radii used here */}
                <h2>List of van der Waals radii used here</h2>
                <table border="1" style={{borderCollapse: "collapse", textAlign: "left"}}>
                    <tr>
                        <td>Br</td>
                        <td>1.85 Å</td>
                    </tr>
                    <tr>
                        <td>C</td>
                        <td>1.7 Å</td>
                    </tr>
                    <tr>
                        <td>Cl</td>
                        <td>1.75 Å</td>
                    </tr>
                    <tr>
                        <td>F</td>
                        <td>1.47 Å</td>
                    </tr>
                    <tr>
                        <td>I</td>
                        <td>1.98 Å</td>
                    </tr>
                    <tr>
                        <td>N</td>
                        <td>1.55 Å</td>
                    </tr>
                    <tr>
                        <td>O</td>
                        <td>1.52 Å</td>
                    </tr>
                    <tr>
                        <td>S</td>
                        <td>1.80 Å</td>
                    </tr>
                    <tr>
                        <td>P</td>
                        <td>1.80 Å</td>
                    </tr>
                </table>
                <br/>
                {/* List of van der Waals radii used here */}

                <h2>
                    For interaction detection, we relied on a combination of external and in-house tools:
                </h2>
                <ul>
                    <li>Reduce v4.14 – for adding hydrogen atoms to proteins and nucleic acids.</li>
                    <li>HBPLUS v3.2 – for detecting hydrogen bonds and water-mediated contacts.</li>
                    <li>NACCESS v2.1.1 – for solvent accessible surface area calculations.</li>
                    <li>HBADD – for additional hydrogen bond analyses.</li>
                </ul>

                <h2>
                    Handling of overlapping classifications
                </h2>
                <p>
                    When more than one interaction type is detected for a residue pair, we apply the following hierarchy:
                </p>
                <ul>
                    <li>If π–π stacking is present together with apolar van der Waals (vdW) contacts, only the π–π stacking is retained.</li>
                    <li>If both a salt bridge and a hydrogen bond are identified for the same atom pair, the contact will be classified as a salt bridge.</li>
                    <li>If hydrogen bonds, halogen bonds, or weak H-bonds coexist with polar vdW contacts, only the directional bonds are kept.</li>
                </ul>
                <p>
                    Clashes are defined when the observed interatomic distance is less than the sum of the van der Waals radii. If the interaction also falls into one of the standard categories (e.g., hydrogen bond, CH–O/N bond,
                    polar/apolar vdW, halogen bond, disulfide bond, salt bridge), it is reported with an asterisk alongside the respective interaction label.
                </p>


                {/* Interface area and residues  */}
                <h2>Interface area and residues </h2>
                <p>Buried surface areas are calculated with NACCESS (Hubbard, S.J. and Thornton, J.M. 'NACCESS' Computer Program. 1993). Residues at the interface are defined as those having an ASA (Accessible Surface Area) decreased by >
                    1.0 Å² upon the complex formation.</p>
                <p>POLAR and NON POLAR Interface percentages (%) refer to the calculated Interface area (Å²) for the complex and sum up to 100.</p>
                <p>In the ASA Tables for chain 1 & 2, ASA values in the complex and in the isolated molecule (“free”), along with the difference between them are reported for each residue.</p>
                {/* Interface area and residues  */}

                <h2>Runtime performances</h2>
                <p>
                    The average runtime for typical protein–protein interface analyses (molecule length in the order of hundreds of residues) is less than 1 minute, depending on the number of atoms and chains involved. The runtime increases
                    to a few minutes for large complexes, such as those involving ribosomal RNAs, whose length is in the order of thousands of residues. Benchmarking runs on our current hardware (64 GB RAM, Intel® Core™ i7-11850H @ 2.50
                    GHz) yielded the following examples:
                </p>
                <ul>
                    <li>1A1T (chains A vs. B, 20 vs 55 ress): ~7 s</li>
                    <li>1B23 (chains R vs. P, 74 vs 405 ress): ~26 s</li>
                    <li>1BJ1 (chains K vs. J, 231 vs 214 ress): ~16 s</li>
                    <li>1OLG (chains A vs. B, 42 vs 42 ress): ~4–5 s</li>
                    <li>11AS (chains A vs. B, 330 vs 330 ress): ~27–28 s</li>
                    <li>1s72 (chains K vs 0, 132 vs 2,922 ress): ~480 s</li>
                </ul>

                <p>
                    Each case was repeated three times, and the observed runtimes were consistent.
                </p>
                <p>
                    Scalability: For larger cryo-EM or multimeric structures, runtime increases approximately linearly with atom count. Complexes with hundreds of thousands of atoms remain feasible within the 40 MB upload limit.
                </p>
                <p>
                    Concurrency management: At present, the server handles jobs sequentially on the front end, with typical wait times remaining short due to the rapid runtime per job. For larger-scale use cases (e.g., batch analyses), we
                    recommend running the pipeline locally, which avoids any queuing delays.
                </p>
                <p>
                    Maximum supported file size: For uploaded structures, the current webserver accepts input files up to 40 MB. For structures retrieved directly from the Protein Data Bank (PDB), there is no hard file size restriction, and
                    the tool can process large files with a high number of atoms.”
                </p>

            </div>
        </>
    )
}